import { AxiosError } from "axios";
import { http } from "@/lib/http";
import { withIdempotencyKey } from "@/lib/idempotency";
import { isMockEnabled } from "@/lib/mock-flags";
import { NoSeatsAvailableError } from "@/lib/mocks/agencySeats";
import * as mock from "../mocks/fixtures";
import type {
  AgencyBrokerListItem,
  BrokerLinkType,
  BrokerPerformance,
  AgencySeatSummary,
} from "../mocks/fixtures";

export const listBrokers = async (): Promise<AgencyBrokerListItem[]> => {
  if (isMockEnabled("agency")) return mock.listBrokers();
  const { data } = await http.get<AgencyBrokerListItem[]>("/brokers");
  return data;
};

export const getBrokerPerformance = async (
  id: string,
): Promise<BrokerPerformance> => {
  if (isMockEnabled("agency")) return mock.getBrokerPerformance(id);
  const { data } = await http.get<BrokerPerformance>(`/brokers/${id}/performance`);
  return data;
};

export const getSeats = async (): Promise<AgencySeatSummary> => {
  if (isMockEnabled("agency")) return mock.getSeats();
  const { data } = await http.get<AgencySeatSummary>("/brokers/seats");
  return data;
};

/**
 * Recusa do convite vinda do crm-api, repassada pelo agency-api (hub#686). O seat
 * reservado já voltou para o plano — só falta explicar o motivo para a imobiliária.
 */
export class InviteRejectedError extends Error {
  constructor(readonly code: string) {
    super(code);
    this.name = "InviteRejectedError";
  }
}

export const inviteBroker = async (
  email: string,
  linkType: BrokerLinkType,
  idempotencyKey?: string,
): Promise<AgencyBrokerListItem> => {
  if (isMockEnabled("agency")) return mock.inviteBroker(email, linkType);
  try {
    const { data } = await http.post<AgencyBrokerListItem>(
      "/brokers/invite",
      { email, linkType },
      withIdempotencyKey(idempotencyKey),
    );
    return data;
  } catch (error) {
    // 409 = plano sem seats disponíveis. Normaliza pro mesmo erro do mock,
    // que a InviteBrokerModal já reconhece pelo `name` pra exibir a msg amigável.
    if (error instanceof AxiosError && error.response?.status === 409) {
      throw new NoSeatsAvailableError();
    }
    // 422 = o crm recusou (corretor já vinculado a outra imobiliária, ou e-mail já
    // com conta). Cada código tem uma explicação própria na modal.
    if (error instanceof AxiosError && error.response?.status === 422) {
      const code = (error.response.data as { code?: string } | undefined)?.code;
      if (code) throw new InviteRejectedError(code);
    }
    throw error;
  }
};

export const resendInvite = async (inviteId: string): Promise<void> => {
  if (isMockEnabled("agency")) return mock.resendInvite(inviteId);
  await http.post(`/brokers/invites/${inviteId}/resend`);
};

export const revokeInvite = async (inviteId: string): Promise<void> => {
  if (isMockEnabled("agency")) return mock.revokeInvite(inviteId);
  await http.delete(`/brokers/invites/${inviteId}`);
};

export const activateLicense = async (
  brokerId: string,
  idempotencyKey?: string,
): Promise<void> => {
  if (isMockEnabled("agency")) return mock.activateLicense(brokerId);
  await http.post("/licenses/activate", { brokerId }, withIdempotencyKey(idempotencyKey));
};

export type {
  AgencyBrokerListItem,
  BrokerLinkType,
  BrokerPerformance,
  AgencySeatSummary,
} from "../mocks/fixtures";
